import { SxProps, Theme } from "@mui/material";
import securityTokens, { securityResponsiveTokens } from "./Security.tokens";
import {
  PHONE_SM_QUERY,
  settingsShellResponsiveTokens,
} from "screens/Settings/Settings.tokens";

const {
  card,
  heading,
  description,
  field,
  requirements,
  sessionChoice,
  primaryButton,
  outlinedButton,
  leadingIcon,
  pill,
  table,
  emptyState,
  pagination,
  rowAction,
} = securityTokens;

const { phone, tablet } = securityResponsiveTokens;

/** Card padding / body gap, per breakpoint (`md` restates the desktop value). */
const cardPadding = {
  xs: phone.card.padding.std,
  sm: tablet.card.padding,
  md: card.padding,
};
const compactCardPadding = {
  xs: phone.card.padding.std,
  sm: tablet.card.compactPadding,
  md: card.padding,
};
const cardBodyGap = {
  xs: phone.card.bodyGap.std,
  sm: tablet.card.bodyGap,
  md: card.bodyGap,
};
/** Outlined actions ("Set Up", "View QR Code", "Disable") share one box. */
const outlinedMetrics = {
  height: {
    xs: phone.compactButton.height.std,
    sm: tablet.compactButton.height,
    md: outlinedButton.height,
  },
  px: {
    xs: phone.compactButton.paddingX.std,
    sm: tablet.compactButton.paddingX,
    md: outlinedButton.paddingX,
  },
  radius: {
    xs: outlinedButton.radius,
    sm: tablet.compactButton.radius,
    md: outlinedButton.radius,
  },
  fontSize: {
    xs: phone.compactButton.fontSize.std,
    sm: tablet.compactButton.fontSize,
    md: outlinedButton.fontSize,
  },
  narrow: {
    height: phone.compactButton.height.sm,
    minHeight: phone.compactButton.height.sm,
    paddingLeft: phone.compactButton.paddingX.sm,
    paddingRight: phone.compactButton.paddingX.sm,
    fontSize: phone.compactButton.fontSize.sm,
  },
};

const tableGutter = {
  xs: phone.table.gutter.std,
  sm: tablet.table.gutter,
  md: table.gutter,
};

/** Shared white card chrome (border, radius, shadow) for every block. */
const cardChrome = {
  bgcolor: card.bg,
  border: `${card.borderWidth} solid ${card.border}`,
  borderRadius: card.radius,
  boxShadow: card.shadow,
  boxSizing: "border-box",
} as const;

/** Same rhythm `SettingsLayout` puts between its own stacked cards. */
const shellPhone = settingsShellResponsiveTokens.phone;
const stackGap = {
  xs: shellPhone.contentGap.std,
  sm: settingsShellResponsiveTokens.tablet.contentGap,
  md: 2.5,
};

const securityStyles = {
  /**
   * The tablet reference stands Change Password beside a rail holding MFA and
   * Passkeys. Phone and desktop keep the plain stack, so the grid collapses to
   * one column at both ends.
   */
  columns: {
    display: "grid",
    gridTemplateColumns: {
      xs: "minmax(0, 1fr)",
      sm: "minmax(0, 1fr) minmax(0, 1fr)",
      md: "minmax(0, 1fr)",
    },
    alignItems: "start",
    gap: stackGap,
    [PHONE_SM_QUERY]: { gap: shellPhone.contentGap.sm },
  } as SxProps<Theme>,

  /** MFA + Passkeys, stacked inside the tablet's right-hand rail. */
  sideColumn: {
    display: "flex",
    flexDirection: "column",
    gap: stackGap,
    minWidth: 0,
    [PHONE_SM_QUERY]: { gap: shellPhone.contentGap.sm },
  } as SxProps<Theme>,

  /** Fully padded card stacking its children vertically (Change Password). */
  formCard: {
    ...cardChrome,
    p: cardPadding,
    display: "flex",
    flexDirection: "column",
    gap: cardBodyGap,
    [PHONE_SM_QUERY]: {
      padding: phone.card.padding.sm,
      gap: phone.card.bodyGap.sm,
    },
  } as SxProps<Theme>,

  /**
   * MFA card. The desktop reference lays it out as glyph · text · action on one
   * line; both small references drop the glyph and stack title+pill, the
   * sentence and the action instead.
   */
  rowCard: {
    ...cardChrome,
    p: compactCardPadding,
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    alignItems: { xs: "flex-start", md: "center" },
    gap: { xs: phone.card.headerGap.std, sm: tablet.card.headerGap, md: leadingIcon.gap },
    [PHONE_SM_QUERY]: {
      padding: phone.card.padding.sm,
      gap: phone.card.headerGap.sm,
    },
  } as SxProps<Theme>,

  /** Header + body card whose body owns its own padding (tables, empty state). */
  splitCard: {
    ...cardChrome,
    overflow: "hidden",
  } as SxProps<Theme>,

  cardHeader: {
    display: "flex",
    flexDirection: "column",
    gap: heading.gap,
  } as SxProps<Theme>,

  /** Split-card header: text on the left, action pushed to the right. */
  splitCardHeader: {
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    alignItems: { xs: "flex-start", md: "center" },
    gap: {
      xs: phone.card.headerGap.std,
      sm: tablet.card.headerGap,
      md: card.headerGap,
    },
    p: {
      xs: phone.card.headerPadding.std,
      sm: tablet.card.headerPadding,
      md: card.headerPadding,
    },
    [PHONE_SM_QUERY]: {
      gap: phone.card.headerGap.sm,
      padding: phone.card.headerPadding.sm,
    },
  } as SxProps<Theme>,

  splitCardHeaderText: {
    display: "flex",
    flexDirection: "column",
    gap: heading.tightGap,
    minWidth: 0,
  } as SxProps<Theme>,

  cardTitle: {
    color: heading.color,
    fontSize: {
      xs: phone.heading.fontSize.std,
      sm: tablet.heading.fontSize,
      md: heading.fontSize,
    },
    [PHONE_SM_QUERY]: { fontSize: phone.heading.fontSize.sm },
    fontWeight: heading.fontWeight,
    letterSpacing: heading.letterSpacing,
    lineHeight: heading.lineHeight,
  } as SxProps<Theme>,

  cardDescription: {
    color: description.color,
    fontSize: {
      xs: phone.description.fontSize.std,
      sm: tablet.description.fontSize,
      md: description.fontSize,
    },
    lineHeight: description.lineHeight,
    [PHONE_SM_QUERY]: { fontSize: phone.description.fontSize.sm },
  } as SxProps<Theme>,

  /* ---------------------------------------------------------------- fields */

  /** Two-column password grid; the first field deliberately sits alone. */
  fieldGrid: {
    display: "grid",
    gridTemplateColumns: {
      xs: "minmax(0, 1fr)",
      md: "minmax(0, 1fr) minmax(0, 1fr)",
    },
    rowGap: {
      xs: phone.card.bodyGap.std,
      sm: tablet.field.rowGap,
      md: field.rowGap,
    },
    columnGap: field.columnGap,
    [PHONE_SM_QUERY]: { rowGap: phone.card.bodyGap.sm },
  } as SxProps<Theme>,

  fieldLabel: {
    color: field.labelColor,
    fontSize: {
      xs: phone.field.labelFontSize.std,
      sm: tablet.field.labelFontSize,
      md: field.labelFontSize,
    },
    [PHONE_SM_QUERY]: { fontSize: phone.field.labelFontSize.sm },
    fontWeight: field.labelFontWeight,
    lineHeight: "normal",
    ml: 0,
    "& .MuiFormLabel-asterisk": { color: field.requiredColor },
  } as SxProps<Theme>,

  textField: {
    "& .MuiOutlinedInput-root": {
      height: {
        xs: phone.field.height.std,
        sm: tablet.field.height,
        md: field.height,
      },
      p: 0,
      borderRadius: {
        xs: field.radius,
        sm: tablet.field.radius,
        md: field.radius,
      },
      bgcolor: field.bg,
      [PHONE_SM_QUERY]: { height: phone.field.height.sm },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: field.border,
        borderWidth: "1px",
      },
      "&:hover": {
        bgcolor: field.bg,
        "& .MuiOutlinedInput-notchedOutline": { borderColor: field.border },
      },
      "&.Mui-focused": {
        bgcolor: field.bg,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: field.focusBorder,
          borderWidth: "1px",
        },
      },
      // Keep the validation outline visible — the base rules above are more
      // specific than MUI's own error styling.
      "&.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: "error.main",
      },
      "& .MuiInputAdornment-root": {
        marginRight: {
          xs: field.paddingX,
          sm: tablet.field.paddingX,
          md: field.paddingX,
        },
      },
    },
    "& .MuiOutlinedInput-input": {
      height: "100%",
      boxSizing: "border-box",
      p: {
        xs: `0 ${field.paddingX}`,
        sm: `0 ${tablet.field.paddingX}`,
        md: `0 ${field.paddingX}`,
      },
      fontSize: {
        xs: phone.field.fontSize.std,
        sm: tablet.field.fontSize,
        md: field.fontSize,
      },
      fontWeight: field.fontWeight,
      color: field.color,
      [PHONE_SM_QUERY]: { fontSize: phone.field.fontSize.sm },
      "&::placeholder": {
        color: field.placeholder,
        opacity: 1,
        fontSize: {
          xs: phone.field.fontSize.std,
          sm: tablet.field.fontSize,
          md: field.fontSize,
        },
        fontWeight: field.fontWeight,
      },
    },
    "& .MuiFormHelperText-root": {
      ml: 0,
      mt: "4px",
      fontSize: field.errorFontSize,
    },
  } as SxProps<Theme>,

  /* -------------------------------------------------- password requirements */

  requirementsPanel: {
    bgcolor: requirements.bg,
    border: `1px solid ${requirements.border}`,
    borderRadius: requirements.radius,
    p: {
      xs: phone.requirements.padding.std,
      sm: tablet.requirements.padding,
      md: requirements.padding,
    },
    display: "grid",
    // Both small references run the checklist as one column — the tablet card
    // is a ~280px rail, not the full content width.
    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
    rowGap: {
      xs: phone.requirements.rowGap,
      sm: tablet.requirements.rowGap,
      md: requirements.rowGap,
    },
    columnGap: requirements.columnGap,
    [PHONE_SM_QUERY]: { padding: phone.requirements.padding.sm },
  } as SxProps<Theme>,

  requirementRow: (met: boolean): SxProps<Theme> => ({
    display: "flex",
    alignItems: "center",
    gap: requirements.gap,
    fontSize: {
      xs: phone.requirements.fontSize.std,
      sm: tablet.requirements.fontSize,
      md: requirements.fontSize,
    },
    lineHeight: "normal",
    color: met ? requirements.metColor : requirements.color,
    [PHONE_SM_QUERY]: { fontSize: phone.requirements.fontSize.sm },
  }),

  requirementDot: (met: boolean): SxProps<Theme> => ({
    width: requirements.dotSize,
    height: requirements.dotSize,
    borderRadius: "50%",
    flexShrink: 0,
    bgcolor: met ? requirements.metDotColor : requirements.dotColor,
  }),

  /* ------------------------------------------------------- session choice */

  sessionChoiceGroup: {
    display: "flex",
    flexDirection: "column",
    gap: sessionChoice.groupGap,
  } as SxProps<Theme>,

  sessionChoiceLabel: {
    color: field.labelColor,
    fontSize: field.labelFontSize,
    fontWeight: field.labelFontWeight,
    lineHeight: "normal",
  } as SxProps<Theme>,

  sessionChoiceRow: {
    display: "flex",
    // Side by side needs the full content width; the tablet card is a ~280px
    // rail, so the two options stack there as well as on the phone.
    flexDirection: { xs: "column", md: "row" },
    gap: sessionChoice.optionGap,
    m: 0,
    flexWrap: "nowrap",
  } as SxProps<Theme>,

  // `font: inherit` must land first — buttons do not inherit the page font and
  // the shorthand would otherwise reset what the type tokens below set.
  sessionChoiceOption: (selected: boolean): SxProps<Theme> => ({
    font: "inherit",
    appearance: "none",
    flex: 1,
    display: "flex",
    alignItems: "flex-start",
    gap: sessionChoice.contentGap,
    p: sessionChoice.padding,
    borderRadius: sessionChoice.radius,
    border: `${sessionChoice.borderWidth} solid ${
      selected ? sessionChoice.borderSelected : sessionChoice.border
    }`,
    bgcolor: selected ? sessionChoice.bgSelected : sessionChoice.bg,
    cursor: "pointer",
    textAlign: "left",
    minWidth: 0,
    "&:focus-visible": {
      outline: `2px solid ${sessionChoice.borderSelected}`,
      outlineOffset: "2px",
    },
  }),

  sessionChoiceRadio: (selected: boolean): SxProps<Theme> => ({
    width: sessionChoice.radioSize,
    height: sessionChoice.radioSize,
    borderRadius: "50%",
    border: `${sessionChoice.radioBorderWidth} solid ${
      selected ? sessionChoice.radioBorderSelected : sessionChoice.radioBorder
    }`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    mt: sessionChoice.radioOffsetTop,
    boxSizing: "border-box",
  }),

  sessionChoiceRadioDot: (selected: boolean): SxProps<Theme> => ({
    width: sessionChoice.radioDotSize,
    height: sessionChoice.radioDotSize,
    borderRadius: "50%",
    bgcolor: selected ? sessionChoice.radioDot : "transparent",
  }),

  sessionChoiceText: {
    display: "flex",
    flexDirection: "column",
    gap: sessionChoice.textGap,
    minWidth: 0,
  } as SxProps<Theme>,

  sessionChoiceTitle: {
    fontSize: sessionChoice.titleFontSize,
    fontWeight: sessionChoice.titleFontWeight,
    color: sessionChoice.titleColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  sessionChoiceDescription: {
    fontSize: sessionChoice.descFontSize,
    color: sessionChoice.descColor,
    lineHeight: sessionChoice.descLineHeight,
  } as SxProps<Theme>,

  /* -------------------------------------------------------------- buttons */

  /** The submit row is left-aligned in the design, so it needs no wrapper rule. */
  primaryButton: {
    alignSelf: "flex-start",
    height: {
      xs: phone.primaryButton.height.std,
      sm: tablet.primaryButton.height,
      md: primaryButton.height,
    },
    minHeight: {
      xs: phone.primaryButton.height.std,
      sm: tablet.primaryButton.height,
      md: primaryButton.height,
    },
    px: {
      xs: phone.primaryButton.paddingX.std,
      sm: tablet.primaryButton.paddingX,
      md: primaryButton.paddingX,
    },
    py: 0,
    [PHONE_SM_QUERY]: {
      height: phone.primaryButton.height.sm,
      minHeight: phone.primaryButton.height.sm,
      paddingLeft: phone.primaryButton.paddingX.sm,
      paddingRight: phone.primaryButton.paddingX.sm,
      fontSize: phone.primaryButton.fontSize.sm,
    },
    bgcolor: primaryButton.bg,
    color: primaryButton.color,
    border: "none",
    borderRadius: {
      xs: primaryButton.radius,
      sm: tablet.primaryButton.radius,
      md: primaryButton.radius,
    },
    fontSize: {
      xs: phone.primaryButton.fontSize.std,
      sm: tablet.primaryButton.fontSize,
      md: primaryButton.fontSize,
    },
    fontWeight: primaryButton.fontWeight,
    lineHeight: "normal",
    textTransform: "none",
    boxShadow: "none",
    whiteSpace: "nowrap",
    "&:hover": { bgcolor: primaryButton.bg, boxShadow: "none" },
    "&.Mui-disabled": {
      bgcolor: primaryButton.disabledBg,
      color: primaryButton.disabledColor,
    },
  } as SxProps<Theme>,

  /** Filled action inside a card header ("Create passkey"). */
  compactPrimaryButton: {
    ml: { xs: 0, md: "auto" },
    flexShrink: 0,
    height: {
      xs: phone.compactButton.height.std,
      sm: tablet.compactButton.height,
      md: primaryButton.compactHeight,
    },
    minHeight: {
      xs: phone.compactButton.height.std,
      sm: tablet.compactButton.height,
      md: primaryButton.compactHeight,
    },
    px: {
      xs: phone.compactButton.paddingX.std,
      sm: tablet.compactButton.paddingX,
      md: primaryButton.compactPaddingX,
    },
    py: 0,
    [PHONE_SM_QUERY]: {
      height: phone.compactButton.height.sm,
      minHeight: phone.compactButton.height.sm,
      paddingLeft: phone.compactButton.paddingX.sm,
      paddingRight: phone.compactButton.paddingX.sm,
      fontSize: phone.compactButton.fontSize.sm,
    },
    bgcolor: primaryButton.bg,
    color: primaryButton.color,
    border: "none",
    borderRadius: {
      xs: primaryButton.radius,
      sm: tablet.compactButton.radius,
      md: primaryButton.radius,
    },
    fontSize: {
      xs: phone.compactButton.fontSize.std,
      sm: tablet.compactButton.fontSize,
      md: primaryButton.compactFontSize,
    },
    fontWeight: primaryButton.fontWeight,
    lineHeight: "normal",
    textTransform: "none",
    boxShadow: "none",
    whiteSpace: "nowrap",
    "&:hover": { bgcolor: primaryButton.bg, boxShadow: "none" },
    "&.Mui-disabled": {
      bgcolor: primaryButton.disabledBg,
      color: primaryButton.disabledColor,
    },
  } as SxProps<Theme>,

  buttonSpinner: {
    color: "inherit",
    mr: primaryButton.spinnerGap,
  } as SxProps<Theme>,

  /** Brand outlined action ("Set Up"). */
  outlinedButton: {
    flexShrink: 0,
    height: outlinedMetrics.height,
    minHeight: outlinedMetrics.height,
    px: outlinedMetrics.px,
    py: 0,
    [PHONE_SM_QUERY]: outlinedMetrics.narrow,
    bgcolor: outlinedButton.bg,
    border: `1px solid ${outlinedButton.border}`,
    borderRadius: outlinedMetrics.radius,
    color: outlinedButton.color,
    fontSize: outlinedMetrics.fontSize,
    fontWeight: outlinedButton.fontWeight,
    lineHeight: "normal",
    textTransform: "none",
    whiteSpace: "nowrap",
    "&:hover": {
      bgcolor: outlinedButton.hoverBg,
      borderColor: outlinedButton.border,
    },
  } as SxProps<Theme>,

  /** Neutral outlined action ("View QR Code"). */
  neutralOutlinedButton: {
    flexShrink: 0,
    height: outlinedMetrics.height,
    minHeight: outlinedMetrics.height,
    px: outlinedMetrics.px,
    py: 0,
    [PHONE_SM_QUERY]: outlinedMetrics.narrow,
    bgcolor: outlinedButton.bg,
    border: `1px solid ${outlinedButton.neutralBorder}`,
    borderRadius: outlinedMetrics.radius,
    color: outlinedButton.neutralColor,
    fontSize: outlinedMetrics.fontSize,
    fontWeight: outlinedButton.fontWeight,
    lineHeight: "normal",
    textTransform: "none",
    whiteSpace: "nowrap",
    "&:hover": {
      bgcolor: outlinedButton.neutralHoverBg,
      borderColor: outlinedButton.neutralBorder,
    },
  } as SxProps<Theme>,

  /** Destructive outlined action ("Disable"). */
  dangerOutlinedButton: {
    flexShrink: 0,
    height: outlinedMetrics.height,
    minHeight: outlinedMetrics.height,
    px: outlinedMetrics.px,
    py: 0,
    [PHONE_SM_QUERY]: outlinedMetrics.narrow,
    bgcolor: outlinedButton.bg,
    border: `1px solid ${outlinedButton.dangerBorder}`,
    borderRadius: outlinedMetrics.radius,
    color: outlinedButton.dangerColor,
    fontSize: outlinedMetrics.fontSize,
    fontWeight: outlinedButton.fontWeight,
    lineHeight: "normal",
    textTransform: "none",
    whiteSpace: "nowrap",
    "&:hover": {
      bgcolor: outlinedButton.dangerHoverBg,
      borderColor: outlinedButton.dangerHoverBorder,
    },
  } as SxProps<Theme>,

  /** Row of trailing actions in the MFA card. */
  cardActions: {
    ml: { xs: 0, md: "auto" },
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: outlinedButton.gap,
  } as SxProps<Theme>,

  /* -------------------------------------------------- glyphs, pills, states */

  /** Both small references drop the glyph tile entirely. */
  leadingIcon: {
    display: { xs: "none", md: "flex" },
    width: leadingIcon.size,
    height: leadingIcon.size,
    borderRadius: leadingIcon.radius,
    bgcolor: leadingIcon.bg,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  } as SxProps<Theme>,

  leadingIconGlyph: {
    width: leadingIcon.iconSize,
    height: leadingIcon.iconSize,
    flexShrink: 0,
  } as SxProps<Theme>,

  /** Title + status pill on one line, description underneath. */
  mfaText: {
    display: "flex",
    flexDirection: "column",
    gap: heading.tightGap,
    minWidth: 0,
  } as SxProps<Theme>,

  mfaTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: pill.gap,
    flexWrap: "wrap",
  } as SxProps<Theme>,

  statusPill: (enabled: boolean): SxProps<Theme> => ({
    display: "inline-flex",
    alignItems: "center",
    p: { xs: phone.pill.padding.std, sm: tablet.pill.padding, md: pill.padding },
    borderRadius: pill.radius,
    fontSize: {
      xs: phone.pill.fontSize.std,
      sm: tablet.pill.fontSize,
      md: pill.fontSize,
    },
    fontWeight: pill.fontWeight,
    letterSpacing: pill.letterSpacing,
    lineHeight: "normal",
    whiteSpace: "nowrap",
    [PHONE_SM_QUERY]: {
      padding: phone.pill.padding.sm,
      fontSize: phone.pill.fontSize.sm,
    },
    color: enabled ? pill.successColor : pill.dangerColor,
    bgcolor: enabled ? pill.successBg : pill.dangerBg,
  }),

  /** Green "THIS DEVICE" badge trailing the device name. */
  rowBadge: {
    display: "inline-flex",
    alignItems: "center",
    p: pill.rowPadding,
    ml: pill.rowMarginLeft,
    borderRadius: pill.radius,
    fontSize: pill.fontSize,
    fontWeight: pill.fontWeight,
    letterSpacing: pill.rowLetterSpacing,
    lineHeight: "normal",
    whiteSpace: "nowrap",
    color: pill.successColor,
    bgcolor: pill.successBg,
  } as SxProps<Theme>,

  /* --------------------------------------------------------------- tables */

  /**
   * Column strip. Its fixed tracks cannot fit a phone or the tablet's ~280px
   * rail, so below `md` the rows stack and the strip is dropped with them.
   */
  tableHeader: (columns: string): SxProps<Theme> => ({
    display: { xs: "none", md: "grid" },
    gridTemplateColumns: columns,
    px: tableGutter,
    py: table.headerPaddingY,
    bgcolor: table.headerBg,
    borderTop: `1px solid ${table.headerBorder}`,
    borderBottom: `1px solid ${table.headerBorder}`,
    fontSize: table.headerFontSize,
    fontWeight: table.headerFontWeight,
    letterSpacing: table.headerLetterSpacing,
    color: table.headerColor,
    lineHeight: "normal",
  }),

  tableHeaderActionCell: {
    textAlign: "right",
  } as SxProps<Theme>,

  /** Stacked rows lose the column strip, so each value names itself instead. */
  stackedCellLabel: {
    display: { xs: "block", md: "none" },
    fontSize: table.headerFontSize,
    fontWeight: table.headerFontWeight,
    letterSpacing: table.headerLetterSpacing,
    color: table.headerColor,
    textTransform: "uppercase",
    lineHeight: "normal",
    mb: "2px",
  } as SxProps<Theme>,

  /** One row: a grid on desktop, a stacked block below it. */
  tableRow: (columns: string, isLast: boolean): SxProps<Theme> => ({
    display: "grid",
    gridTemplateColumns: { xs: "minmax(0, 1fr)", md: columns },
    alignItems: { xs: "flex-start", md: "center" },
    rowGap: { xs: phone.table.rowGap, md: 0 },
    px: tableGutter,
    py: {
      xs: phone.table.rowPaddingY,
      sm: tablet.table.rowPaddingY,
      md: table.rowPaddingY,
    },
    [PHONE_SM_QUERY]: {
      paddingLeft: phone.table.gutter.sm,
      paddingRight: phone.table.gutter.sm,
    },
    fontSize: table.rowFontSize,
    color: table.rowColor,
    lineHeight: "normal",
    borderBottom: isLast ? "none" : `1px solid ${table.rowDivider}`,
  }),

  deviceCell: {
    display: "flex",
    alignItems: "center",
    gap: table.deviceGap,
    minWidth: 0,
  } as SxProps<Theme>,

  deviceIcon: {
    width: table.deviceIconSize,
    height: table.deviceIconSize,
    flexShrink: 0,
  } as SxProps<Theme>,

  deviceName: {
    fontWeight: table.deviceFontWeight,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  } as SxProps<Theme>,

  mutedCell: {
    color: table.mutedColor,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  actionCell: {
    display: "flex",
    justifyContent: { xs: "flex-start", md: "flex-end" },
    alignItems: "center",
    gap: rowAction.gap,
  } as SxProps<Theme>,

  /** Text link ending a table row ("Sign out"). */
  rowActionLink: {
    font: "inherit",
    p: 0,
    m: 0,
    border: "none",
    bgcolor: "transparent",
    fontSize: table.actionFontSize,
    fontWeight: table.actionFontWeight,
    color: table.actionColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
    cursor: "pointer",
    textTransform: "none",
    minWidth: 0,
    "&:hover": { color: table.actionHoverColor, bgcolor: "transparent" },
    "&.Mui-disabled": { color: pagination.disabledColor },
  } as SxProps<Theme>,

  rowIconButton: (danger: boolean): SxProps<Theme> => ({
    width: rowAction.size,
    height: rowAction.size,
    borderRadius: rowAction.radius,
    color: rowAction.color,
    "&:hover": {
      bgcolor: rowAction.hoverBg,
      color: danger ? rowAction.dangerHoverColor : rowAction.hoverColor,
    },
  }),

  rowIconGlyph: {
    width: rowAction.iconSize,
    height: rowAction.iconSize,
    flexShrink: 0,
  } as SxProps<Theme>,

  tableSkeletonCell: {
    height: table.skeletonHeight,
  } as SxProps<Theme>,

  /* ---------------------------------------------------- empty states, pager */

  /** Centred block shown under the Passkeys header when there are none. */
  emptyState: {
    borderTop: `1px solid ${table.rowDivider}`,
    px: tableGutter,
    py: emptyState.paddingY,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: emptyState.gap,
  } as SxProps<Theme>,

  emptyStateCircle: {
    width: emptyState.circleSize,
    height: emptyState.circleSize,
    borderRadius: "50%",
    bgcolor: emptyState.circleBg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  } as SxProps<Theme>,

  emptyStateIcon: {
    width: emptyState.iconSize,
    height: emptyState.iconSize,
    flexShrink: 0,
  } as SxProps<Theme>,

  emptyStateTitle: {
    fontSize: emptyState.titleFontSize,
    fontWeight: emptyState.titleFontWeight,
    color: emptyState.titleColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  emptyStateDescription: {
    fontSize: emptyState.descFontSize,
    color: emptyState.descColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  /** One-line placeholder replacing the rows of a table. */
  tableEmptyState: {
    px: tableGutter,
    py: emptyState.rowPaddingY,
    textAlign: "center",
    fontSize: emptyState.rowFontSize,
    color: emptyState.rowColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  paginationBar: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    px: tableGutter,
    py: pagination.paddingY,
    borderTop: `1px solid ${pagination.border}`,
  } as SxProps<Theme>,

  pagination: {
    "& .MuiPagination-ul": { gap: pagination.gap, flexWrap: "nowrap" },
    "& .MuiPagination-ul > li": { m: 0 },
    "& .MuiPaginationItem-root": {
      minWidth: pagination.itemSize,
      width: pagination.itemSize,
      height: pagination.itemSize,
      m: 0,
      p: 0,
      borderRadius: pagination.itemRadius,
      bgcolor: "transparent",
      fontSize: pagination.itemFontSize,
      fontWeight: pagination.itemFontWeight,
      color: pagination.itemColor,
      "&:hover": { bgcolor: pagination.itemHoverBg },
    },
    "& .MuiPaginationItem-root.Mui-selected": {
      bgcolor: pagination.selectedBg,
      color: pagination.selectedColor,
      "&:hover": {
        bgcolor: pagination.selectedBg,
        color: pagination.selectedColor,
      },
    },
    "& .MuiPaginationItem-icon": {
      width: pagination.arrowSize,
      height: pagination.arrowSize,
      fontSize: pagination.arrowSize,
    },
    "& .MuiPaginationItem-root.Mui-disabled": {
      color: pagination.disabledColor,
      opacity: 1,
    },
  } as SxProps<Theme>,
};

export default securityStyles;
